import React, { useEffect, useState, useRef } from "react";

const About = () => {
  const sections = [
    {
      id: "mission",
      title: "Our Mission",
      content:
        "At Codex Future Technologies Pvt. Ltd., we empower businesses to succeed in the digital world through innovative, scalable, and user-focused IT solutions.",
      image: "/our-mission.jpg",
    },
    {
      id: "vision",
      title: "Our Vision",
      content:
        "We aim to be the most trusted technology partner, delivering high-quality web development, mobile applications, digital marketing, and cloud solutions to help businesses grow.",
      image: "/our-vission.jpg",
    },
    {
      id: "values",
      title: "Our Values",
      content:
        "💡 We’re not just developers—we’re your technology partner for the future. Creativity, technical expertise, and integrity drive everything we do.",
      image: "/partner.jpg",
    },
    {
      id: "experience",
      title: "What We Offer",
      content:
        "From startups to enterprises, we help organizations embrace digital transformation through web development, mobile app development, digital marketing, and cloud-based solutions.",
      image: "/offerAbout.jpg",
    },
    {
      id: "future",
      title: "Our Promise",
      content:
        "At Codex Future Technologies, we believe in building long-term partnerships by providing continuous support, innovation, and value-driven services. 🚀",
      image: "/promise.jpg",
    },
  ];

  const sectionRefs = useRef([]);
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const idx = sectionRefs.current.indexOf(entry.target);
            if (idx !== -1) setActiveIndex(idx);
          }
        });
      },
      { threshold: 0.4 }
    );

    sectionRefs.current.forEach((section) => section && observer.observe(section));
    return () => observer.disconnect();
  }, []);

  const handleScrollDown = () => {
    sectionRefs.current[0]?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div className="relative w-full font-sans overflow-x-hidden bg-blue-50">

      {/* HERO SECTION */}
      <section className="relative w-full min-h-[60vh] flex flex-col justify-center items-center text-center px-4 sm:px-6 bg-black py-32 sm:py-40 md:py-48">
        <h1 className="text-4xl sm:text-6xl lg:text-7xl font-[600] mb-4 uppercase tracking-wide text-white">
          About Us
        </h1>
        <p className="mt-2 text-base sm:text-xl max-w-2xl text-gray-300 px-2">
          At <strong className="text-[#df2313]">Codex Future Technologies</strong>, we deliver innovative web, mobile, and cloud solutions that drive business growth.
        </p>

        <button
          onClick={handleScrollDown}
          className="absolute bottom-6 sm:bottom-8 animate-bounce text-white bg-[#df2313] w-12 h-12 rounded-full flex items-center justify-center shadow-lg hover:scale-110 transition-transform duration-300"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-8 w-8 text-white"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={3}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
          </svg>
        </button>
      </section>

      {/* ABOUT SECTIONS */}
      <div className="relative py-12 sm:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-20">
          {sections.map((section, index) => {
            const isLeft = index % 2 === 0;
            const isActive = index === activeIndex;

            return (
              <div
                key={section.id}
                ref={(el) => (sectionRefs.current[index] = el)}
                className={`flex flex-col ${
                  isLeft ? "md:flex-row" : "md:flex-row-reverse"
                } items-center gap-10 md:gap-16 lg:gap-24 transition-all duration-700`}
                style={{
                  filter: isActive ? "none" : "grayscale(70%) brightness(90%)",
                  transform: isActive ? "scale(1)" : "scale(0.98)",
                }}
              >
                {/* IMAGE */}
                <div className="w-full md:w-1/2 rounded-2xl overflow-hidden shadow-xl hover:scale-105 transition-transform duration-500">
                  <img
                    src={section.image}
                    alt={section.title}
                    className="w-full h-52 sm:h-72 md:h-96 object-cover"
                  />
                </div>

                {/* TEXT */}
                <div className="w-full md:w-1/2 text-center md:text-left px-2">
                  <h2
                    className={`text-2xl sm:text-3xl md:text-4xl font-bold mb-3 ${
                      isActive ? "text-black" : "text-gray-400"
                    }`}
                  >
                    {section.title}
                  </h2>
                  <p
                    className={`text-base sm:text-lg leading-relaxed ${
                      isActive ? "text-gray-700" : "text-gray-400"
                    }`}
                  >
                    {section.content}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* FLOATING BACKGROUND */}
      <div className="absolute inset-0 -z-10">
        <div className="absolute w-[700px] h-[700px] bg-blue-300 rounded-full blur-[180px] opacity-25 top-1/3 -left-40 animate-float-smooth"></div>
        <div className="absolute w-[500px] h-[500px] bg-blue-400 rounded-full blur-[180px] opacity-20 bottom-10 -right-32 animate-float-smooth"></div>
      </div>

      <style>{`
        @keyframes floatSmooth {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-12px); }
        }
        .animate-float-smooth {
          animation: floatSmooth 10s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
};

export default About;